import checkTokens from "@amzn/meridian-tokens/base/icon/check-large";
import closeMediumTokens from "@amzn/meridian-tokens/base/icon/close-medium";
import thumbsDownTokens from "@amzn/meridian-tokens/base/icon/thumbs-down";
import thumbsUpTokens from "@amzn/meridian-tokens/base/icon/thumbs-up";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Text from "@amzn/meridian/text";
import Textarea from "@amzn/meridian/textarea";
import { useEffect, useRef, useState } from "react";
import ChatService from "src/components/pages/home/service/chatService";
import {
  updateResponses,
  updateStreamingHistory,
} from "src/components/pages/home/store/chatSlice";
import {
  ALIAS,
  DOWNVOTE_REASON,
  MESSAGE_ID,
  NO_MATCHED_TOOL,
  SAFER_DOWNVOTE,
  SAFER_UPVOTE,
  TOOL_NAME,
} from "src/constants/constants";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { sendCustomRUMEvent } from "src/utils/awsRUM";
import "./vote.scss";

export interface VoteProps {
  messageId: string;
  llmResponse: { tool_name: string };
  voteResponse: "successful" | "failed" | "";
  thumbsUpClicked: boolean;
  thumbsDownClicked: boolean;
}
const Vote = ({
  messageId,
  llmResponse,
  voteResponse,
  thumbsUpClicked,
  thumbsDownClicked,
}: VoteProps) => {
  const dispatch = useAppDispatch();
  const { alias } = useAppSelector((state) => state.user);
  const [showDownvoteReason, setShowDownvoteReason] = useState(false);
  const [downVoteReason, setDownVoteReason] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    // Only try to focus if showDownvoteReason is true and inputRef.current exists
    if (showDownvoteReason && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showDownvoteReason, inputRef.current]);

  async function handleVoteSubmit(vote: string, reason: string | null = null) {
    if (!messageId) {
      return;
    }

    const response = await ChatService.putVote({
      message_id: messageId,
      vote: vote,
      downvote_reason: reason,
    });

    const toolName =
      llmResponse?.tool_name == undefined
        ? NO_MATCHED_TOOL
        : llmResponse?.tool_name;

    let updatedData;
    if (response.data.successful === true && vote === "UpVote") {
      updatedData = {
        thumbsUpClicked: true,
        thumbsDownClicked: false,
        voteResponse: "successful",
      };

      sendCustomRUMEvent(SAFER_UPVOTE, {
        [ALIAS]: alias,
        [MESSAGE_ID]: messageId,
        [TOOL_NAME]: toolName,
      });
    } else if (response.data.successful === true && vote === "DownVote") {
      updatedData = {
        thumbsUpClicked: false,
        thumbsDownClicked: true,
        voteResponse: "successful",
      };

      sendCustomRUMEvent(SAFER_DOWNVOTE, {
        [ALIAS]: alias,
        [MESSAGE_ID]: messageId,
        [TOOL_NAME]: toolName,
        [DOWNVOTE_REASON]: reason,
      });
    } else {
      updatedData = {
        thumbsUpClicked: false,
        thumbsDownClicked: false,
        voteResponse: "failed",
      };
    }

    const votePayload = {
      message_id: messageId,
      updates: updatedData,
    };
    dispatch(updateResponses(votePayload));
    dispatch(updateStreamingHistory(votePayload));

    const intervalId = setInterval(() => {
      clearInterval(intervalId);
      updatedData = {
        voteResponse: "",
      };
      const votePayload = {
        message_id: messageId,
        updates: updatedData,
      };
      dispatch(updateResponses(votePayload));
      dispatch(updateStreamingHistory(votePayload));
    }, 2000);
  }

  async function handleThumbsButtonClick(vote: string) {
    if (vote === "DownVote") {
      setShowDownvoteReason(true);
    } else {
      await handleVoteSubmit(vote, null);
    }
  }

  const handleDownvoteSubmit = async () => {
    await handleVoteSubmit("DownVote", downVoteReason || null);
    setShowDownvoteReason(false);
    setDownVoteReason("");
  };

  return (
    <div>
      {voteResponse === "successful" && (
        <div className="voting">
          <Button type="icon">
            <Icon tokens={checkTokens} className={"voted"} />
          </Button>
          <Text type="b300" className="vote-info">
            Your feedback has been submitted!
          </Text>
        </div>
      )}
      {voteResponse === "failed" && (
        <div className="voting">
          <Button type="icon">
            <Icon tokens={closeMediumTokens} className={"voted"} />
          </Button>
          <Text type="b300" className="vote-info">
            Failed to submit your vote. Please try again.
          </Text>
        </div>
      )}
      {voteResponse === "" && !showDownvoteReason && (
        <div className="voting">
          <Button
            type="icon"
            onClick={() => handleThumbsButtonClick("UpVote")}
            disabled={thumbsUpClicked}
            id={thumbsUpClicked ? "up-vote-clicked" : "up-vote"}
          >
            <Icon tokens={thumbsUpTokens} />
          </Button>
          <Button
            type="icon"
            onClick={() => handleThumbsButtonClick("DownVote")}
            disabled={thumbsDownClicked}
            id={thumbsDownClicked ? "down-vote-clicked" : "down-vote"}
          >
            <Icon tokens={thumbsDownTokens} />
          </Button>
          <Text type="b200" className="vote-info">
            AI-generated answer. Help us improve.
          </Text>
        </div>
      )}
      {showDownvoteReason && (
        <div className="downvote-feedback">
          <Textarea
            id="downvote-reason-textarea"
            ref={inputRef}
            value={downVoteReason}
            onChange={(value) => setDownVoteReason(value)}
            placeholder="Please tell us why you vote down this reply. We value your input to provide better answer next time."
            rows={2}
            resize="auto"
            aria-label="Downvote reason"
          />
          <div className="feedback-buttons">
            <Button onClick={() => setShowDownvoteReason(false)}>Cancel</Button>
            <Button onClick={handleDownvoteSubmit}>Submit</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vote;
